'use client';

import { useMemo } from 'react';
import { Check } from 'lucide-react';
import type { FormAnswers, ServeForm, ServeFormField } from '@/lib/types';
import { sanitizeHtml, toPlainText } from '@/lib/richText';
import { ToggleChip } from './chips';
import { RichDescription } from './RichDescription';

/**
 * An MP custom form, rendered the way the stock opportunity widget renders
 * it: fields in order, conditional fields folding in when their gate has
 * the right answer, a group's children under their Instructions heading.
 * Answers are plain strings keyed by field id (checkboxes are "Yes"), the
 * shape POST /api/serve/[id]/responses validates and writes.
 *
 * Labels come from MP as HTML. `label` is the plain text; `labelHtml` is
 * set when the markup still says something (bold, a link, a line break)
 * and is rendered sanitized. Woodside's multiselect hack (labels wrapped
 * in `<div class="multiselect…">`) arrives already unwrapped.
 */

interface FormFieldsProps {
  form: ServeForm;
  answers: FormAnswers;
  onChange: (next: FormAnswers) => void;
  /** 'dark' is reserved for branded panes; serve forms are light today */
  tone?: 'light';
}

const FIELD =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-[#1c2b39] placeholder:text-gray-400 focus:border-[var(--accent,#299c8f)] focus:ring-2 focus:ring-[color-mix(in_srgb,var(--accent,#299c8f)_30%,transparent)] focus:outline-none';
const LABEL = 'text-xs font-bold tracking-widest text-gray-500 uppercase';

const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

export function childrenOf(form: ServeForm, field: ServeFormField): ServeFormField[] {
  return form.fields.filter((f) => f.parentId === field.id && !f.hidden);
}

/** Mirrors apps/api/src/lib/mpForms.ts `isVisible`. */
export function isVisible(form: ServeForm, field: ServeFormField, answers: FormAnswers): boolean {
  if (field.hidden) return false;
  if (field.parentId !== null) {
    const parent = form.fields.find((f) => f.id === field.parentId);
    if (parent && !isVisible(form, parent, answers)) return false;
  }
  if (field.dependsOn === null) return true;
  const gate = form.fields.find((f) => f.id === field.dependsOn);
  if (!gate) return true;
  if (!isVisible(form, gate, answers)) return false;
  return same(toPlainText(answers[String(gate.id)] ?? ''), toPlainText(field.dependsOnValue ?? ''));
}

/** A child of a group counts as answered when ticked, or when typed into. */
function childAnswered(c: ServeFormField, answers: FormAnswers): boolean {
  const v = answers[String(c.id)] ?? '';
  return c.kind === 'checkbox' ? same(v, 'Yes') : Boolean(v.trim());
}

/** The visible fields still missing a required answer, for the Send button. */
export function missingRequired(form: ServeForm, answers: FormAnswers): ServeFormField[] {
  return form.fields.filter((f) => {
    if (!f.required || !isVisible(form, f, answers) || f.parentId !== null) return false;
    if (f.kind === 'instructions') {
      const kids = childrenOf(form, f);
      return kids.length > 0 && !kids.some((c) => childAnswered(c, answers));
    }
    if (f.kind === 'checkbox') return !same(answers[String(f.id)], 'Yes');
    return !(answers[String(f.id)] ?? '').trim();
  });
}

function Required() {
  return (
    <span className="text-[var(--accent-text,var(--accent,#299c8f))]" aria-hidden="true">
      {' '}
      *
    </span>
  );
}

/** A field's label: the plain text, or its markup when that carries meaning. */
function FieldLabel({ field }: { field: ServeFormField }) {
  const html = useMemo(
    () => (field.labelHtml ? sanitizeHtml(field.labelHtml) : null),
    [field.labelHtml]
  );
  if (html === null) return <>{field.label}</>;
  return (
    <span
      className="[&_a]:font-semibold [&_a]:text-[var(--accent-text,var(--accent,#299c8f))] [&_a]:underline [&_p]:inline [&_strong]:font-bold"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function CheckRow({
  field,
  checked,
  onChange,
}: {
  field: ServeFormField;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left text-sm text-[#1c2b39] transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.99]"
    >
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
          checked
            ? 'border-[var(--accent,#299c8f)] bg-[var(--accent,#299c8f)] text-[var(--on-accent,#fff)]'
            : 'border-gray-300 bg-white'
        }`}
      >
        {checked && <Check className="h-3.5 w-3.5" strokeWidth={3.5} />}
      </span>
      <span>
        <FieldLabel field={field} />
        {field.required && field.parentId === null && <Required />}
      </span>
    </button>
  );
}

const CHIP_SELECT_MAX = 8;

interface ControlProps {
  field: ServeFormField;
  value: string;
  setAnswer: (id: number, value: string) => void;
  /** Inside a group: the parent decides what is required */
  nested?: boolean;
}

/** One typed or picked field with its label: text, memo, date, phone, radio, select. */
function Control({ field, value, setAnswer, nested = false }: ControlProps) {
  const required = field.required && !nested;
  const heading = (
    <>
      <FieldLabel field={field} />
      {required && <Required />}
    </>
  );

  if (
    field.kind === 'radio' ||
    (field.kind === 'select' && field.options.length <= CHIP_SELECT_MAX)
  ) {
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className={`mb-2 ${LABEL}`}>{heading}</legend>
        <div className="flex flex-wrap gap-2">
          {field.options.map((o) => (
            <ToggleChip
              key={o}
              active={same(value, o)}
              onClick={() => setAnswer(field.id, same(value, o) ? '' : o)}
              accent
            >
              {toPlainText(o)}
            </ToggleChip>
          ))}
        </div>
      </fieldset>
    );
  }

  if (field.kind === 'select') {
    return (
      <label className="flex flex-col gap-2">
        <span className={LABEL}>{heading}</span>
        <select
          value={value}
          onChange={(e) => setAnswer(field.id, e.target.value)}
          className={FIELD}
        >
          <option value="">Choose one</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {toPlainText(o)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.kind === 'file') {
    return (
      <p className="text-sm text-gray-500">
        "{field.label}" asks for a file, which this form cannot take online yet.
      </p>
    );
  }

  return (
    <label className="flex flex-col gap-2">
      <span className={LABEL}>{heading}</span>
      {field.kind === 'memo' ? (
        <textarea
          value={value}
          onChange={(e) => setAnswer(field.id, e.target.value)}
          rows={3}
          maxLength={4000}
          className={FIELD}
        />
      ) : (
        <input
          type={field.kind === 'date' ? 'date' : field.kind === 'phone' ? 'tel' : 'text'}
          value={value}
          onChange={(e) => setAnswer(field.id, e.target.value)}
          maxLength={field.kind === 'date' ? undefined : 500}
          autoComplete={field.kind === 'phone' ? 'tel' : 'off'}
          className={FIELD}
        />
      )}
    </label>
  );
}

export function FormFields({ form, answers, onChange }: FormFieldsProps) {
  const setAnswer = (id: number, value: string) => {
    const next = { ...answers };
    if (value) next[String(id)] = value;
    else delete next[String(id)];
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-5">
      {form.instructions && (
        <RichDescription text={form.instructions} className="text-sm text-gray-600" />
      )}
      {form.fields.map((field) => {
        if (field.parentId !== null || !isVisible(form, field, answers)) return null;
        const key = String(field.id);
        const value = answers[key] ?? '';

        if (field.kind === 'instructions') {
          const kids = childrenOf(form, field);
          if (kids.length === 0) {
            // Staff write instructions in MP's editor, so they arrive as HTML.
            return (
              <RichDescription
                key={field.id}
                text={field.labelHtml ?? field.label}
                className="text-sm text-gray-600"
              />
            );
          }
          // A group: checkboxes as rows, anything else (a text box for
          // "anything else?") as its own control beneath them.
          return (
            <fieldset key={field.id} className="flex flex-col gap-2">
              <legend className={`mb-2 ${LABEL}`}>
                <FieldLabel field={field} />
                {field.required && <Required />}
              </legend>
              {kids.map((c) =>
                c.kind === 'checkbox' ? (
                  <CheckRow
                    key={c.id}
                    field={c}
                    checked={same(answers[String(c.id)], 'Yes')}
                    onChange={(on) => setAnswer(c.id, on ? 'Yes' : '')}
                  />
                ) : (
                  <div key={c.id} className="mt-2">
                    <Control
                      field={c}
                      value={answers[String(c.id)] ?? ''}
                      setAnswer={setAnswer}
                      nested
                    />
                  </div>
                )
              )}
            </fieldset>
          );
        }

        if (field.kind === 'checkbox') {
          return (
            <div key={field.id}>
              <CheckRow
                field={field}
                checked={same(value, 'Yes')}
                onChange={(on) => setAnswer(field.id, on ? 'Yes' : '')}
              />
            </div>
          );
        }

        return <Control key={field.id} field={field} value={value} setAnswer={setAnswer} />;
      })}
    </div>
  );
}
